import { useState } from "react";

// Minimal responsive SVG line chart (replaces recharts for the one line series
// the dashboard needs). Renders an axis-less trend with hover tooltips.
export function LineChart({
  data,
  labels,
  yMin,
  yMax,
  height = 240,
  color = "#007047",
  valueSuffix = "%",
}: {
  data: number[];
  labels: string[];
  yMin: number;
  yMax: number;
  height?: number;
  color?: string;
  valueSuffix?: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const w = 560;
  const h = height;
  const padX = 34;
  const padTop = 10;
  const padBottom = 26;
  const plotW = w - padX - 8;
  const plotH = h - padTop - padBottom;
  const range = yMax - yMin || 1;
  const x = (i: number) => padX + (i * plotW) / (data.length - 1);
  const y = (v: number) => padTop + plotH - ((v - yMin) / range) * plotH;
  const line = data.map((v, i) => `${x(i)},${y(v)}`).join(" ");
  const ticks = 4;

  return (
    <div className="relative w-full" style={{ height }}>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        className="h-full w-full"
        onMouseLeave={() => setHover(null)}
      >
        {/* horizontal gridlines + y labels */}
        {Array.from({ length: ticks + 1 }).map((_, t) => {
          const val = yMin + (range * t) / ticks;
          const yy = y(val);
          return (
            <g key={t}>
              <line
                x1={padX}
                y1={yy}
                x2={w - 8}
                y2={yy}
                stroke="#000"
                strokeOpacity={0.05}
              />
              <text
                x={padX - 6}
                y={yy + 3}
                textAnchor="end"
                fontSize={11}
                fill="#6B7280"
              >
                {Math.round(val)}
              </text>
            </g>
          );
        })}
        {/* x labels */}
        {labels.map((lbl, i) => (
          <text
            key={i}
            x={x(i)}
            y={h - 8}
            textAnchor="middle"
            fontSize={11}
            fill="#6B7280"
          >
            {lbl}
          </text>
        ))}
        {/* line */}
        <polyline
          points={line}
          fill="none"
          stroke={color}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* points + hover targets */}
        {data.map((v, i) => (
          <g key={i}>
            <circle
              cx={x(i)}
              cy={y(v)}
              r={hover === i ? 4 : 2.5}
              fill={color}
            />
            <rect
              x={x(i) - plotW / (data.length - 1) / 2}
              y={padTop}
              width={plotW / (data.length - 1)}
              height={plotH}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
            />
          </g>
        ))}
      </svg>
      {hover !== null && (
        <div
          className="pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-[6px] border border-[#DED8CC] bg-[#FBF8F1] px-2.5 py-1.5 text-xs shadow-md"
          style={{
            left: `${(x(hover) / w) * 100}%`,
            top: `${(y(data[hover]) / h) * 100}%`,
          }}
        >
          <span className="font-bold text-[#294B39]">
            {data[hover]}
            {valueSuffix}
          </span>{" "}
          <span className="text-[#687368]">{labels[hover]}</span>
        </div>
      )}
    </div>
  );
}
